import './style.scss';

import capitalize from 'lodash/capitalize';
import { useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

import { ApiDexBuySellBot } from 'api';
import { DexBalanceBotContext } from 'context/DexBalanceBotContext';
import { DexBuySellBotContext } from 'context/DexBuySellBotContext';
import { useDexBuyAndSellWalletsRecords } from 'hooks/tables/dex';
import { useTypedDispatch, useTypedSelector } from 'store';
import { useFilters } from 'store/slices/filters/hooks/useFilters';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { ConnectWalletBalanceTaskTable } from 'tables/dex/wallets/ConnectWalletBalanceTaskTable';
import { IDexBuySellBotTask, IDexBuySellBotWallet } from 'types/bots';
import { EDexPairFilters } from 'types/filters';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ConnectNewWalletsToPairForm } from 'ui/forms';
import { Modal } from 'ui/Modal/Modal';
import { ToggleRadio } from 'ui/ToggleRadio/ToggleRadio';

type ConnectWalletsToDexBuySellTaskModalProps = {
  onClose: (selectedRecords?: IDexBuySellBotWallet[]) => void;
  initialConnected?: 'connected' | 'disconnected';
  disableToggler?: boolean;
  readOnlyMode?: boolean;
  task: IDexBuySellBotTask;
  activeWalletsStep?: number;
  includeSteps?: boolean;
};

const ConnectWalletsToDexBuySellTaskModal = ({
  onClose,
  readOnlyMode,
  initialConnected = 'connected',
  disableToggler = false,
  task,
  activeWalletsStep,
  includeSteps,
}: ConnectWalletsToDexBuySellTaskModalProps) => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const dispatch = useTypedDispatch();

  const [isConnected, setIsConnected] = useState<boolean>(initialConnected === 'connected');

  const { saveFilters, clearFilters } = useFilters({
    dex: {
      pair: dexPair,
      type: EDexPairFilters.buyAndSellWallets,
    },
  });

  const taskId = useMemo(() => task.id, [task]);
  const mode = useMemo(() => (isConnected ? 'disconnect' : 'connect'), [isConnected]);

  const { tableRecords, selectedRecords, rowSelection, hasMore, loading, loadMore } =
    useDexBuyAndSellWalletsRecords({
      task_id: taskId,
      is_connected: isConnected,
    });

  useLayoutEffect(() => {
    clearFilters();
    saveFilters({ update: true });

    //eslint-disable-next-line
  }, [includeSteps]);

  const { handleLoadRecords } = useContext(DexBalanceBotContext);
  const { handleUpdateCountWallets } = useContext(DexBuySellBotContext);

  const [requestLoading, setRequestLoading] = useState<boolean>(false);

  const handleAppendAccounts = useCallback(async () => {
    try {
      if (!taskId) return null;

      setRequestLoading(true);

      const selectedWallets = selectedRecords.map(wallet => wallet.id);

      const { isSuccess, errorMessage } = await (mode === 'connect'
        ? ApiDexBuySellBot.addWalletsToBuySellTask({
            taskId: taskId,
            walletIds: selectedWallets,
          })
        : ApiDexBuySellBot.removeWalletsFromBuySellTask({
            taskId: taskId,
            walletIds: selectedWallets,
          }));

      if (isSuccess) {
        onClose();

        handleUpdateCountWallets(taskId, selectedWallets.length, mode);
        handleLoadRecords();

        dispatch(
          setAlertState({
            type: 'success',
            text: `You have successfully ${mode === 'connect' ? 'connected' : 'disconnected'} ${
              selectedRecords.length
            } wallets to pair`,
            onClose: () => {
              dispatch(dropAlertState());
            },
            onSubmit: () => {
              onClose();
              dispatch(dropAlertState());
            },
          }),
        );
      } else {
        onClose();

        dispatch(
          setAlertState({
            type: 'failed-img',
            text: String(errorMessage),
            onClose: () => {
              dispatch(dropAlertState());
            },
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setRequestLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecords, taskId, mode, dispatch, handleLoadRecords, onClose]);

  return (
    <Modal
      title={readOnlyMode ? 'Wallets' : 'Choose wallets'}
      className="mm-dex-wallets-buysell-modal"
      onClose={onClose}
    >
      <ConnectNewWalletsToPairForm>
        <div className="connect-new-wallets-to-pair-modal-content">
          {!readOnlyMode && (
            <ToggleRadio
              disabled={disableToggler}
              titles={['Connected', 'Not connected']}
              name="connected-toggle"
              value={isConnected ? 'Connected' : 'Not connected'}
              onChange={e => setIsConnected(e.target.value === 'Connected')}
            />
          )}

          <ConnectWalletBalanceTaskTable
            mode={mode}
            readOnlyMode={readOnlyMode}
            filtersType={EDexPairFilters.buyAndSellWallets}
            records={tableRecords}
            rowSelection={rowSelection}
            hasMore={hasMore}
            loading={loading}
            loadMore={loadMore}
            selectedRecords={selectedRecords}
          />
        </div>

        {includeSteps ? (
          <div style={{ display: 'flex', gap: '40px' }}>
            <Button onClick={() => onClose(selectedRecords)}>
              {activeWalletsStep === 2 ? capitalize(mode) : 'Next'}
            </Button>
          </div>
        ) : (
          !requestLoading &&
          !readOnlyMode && (
            <Button disabled={selectedRecords.length === 0} onClick={handleAppendAccounts}>
              {capitalize(mode)}
            </Button>
          )
        )}

        {requestLoading && <ButtonLoading />}
      </ConnectNewWalletsToPairForm>
    </Modal>
  );
};

export { ConnectWalletsToDexBuySellTaskModal };
